import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { jobPostingApi } from '@/services/jobPostingService';
import { useApplicationStore } from '@/store/applicationStore';
import { useAuthStore } from '@/store/authStore';

import { JobPostingCard } from './JobPostingCard';

import type { ActiveJobPosting, ActiveJobPostingsQuery, PaginationMeta } from '@/types/jobPosting.types';

const ROLE_OPTIONS = ['armed', 'unarmed', 'supervisor', 'patrol', 'event'] as const;
const STATE_OPTIONS = ['FL', 'NY'] as const;
const SORT_OPTIONS = [
  { value: 'createdAt:desc', labelKey: 'jobPostings.sort.newest' },
  { value: 'createdAt:asc', labelKey: 'jobPostings.sort.oldest' },
  { value: 'title:asc', labelKey: 'jobPostings.sort.titleAZ' },
  { value: 'title:desc', labelKey: 'jobPostings.sort.titleZA' },
  { value: 'role:asc', labelKey: 'jobPostings.sort.role' },
] as const;
const PAGE_LIMIT = 9;

interface JobPostingsListProps {
  limit?: number;
}

export function JobPostingsList({ limit }: JobPostingsListProps) {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const checkExistingApplications = useApplicationStore((state) => state.checkExistingApplications);

  const [postings, setPostings] = useState<ActiveJobPosting[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [appliedJobIds, setAppliedJobIds] = useState<Set<string>>(new Set());

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortValue, setSortValue] = useState('createdAt:desc');
  const [page, setPage] = useState(1);
  const [cityInput, setCityInput] = useState('');

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const cityDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pageLimit = limit ?? PAGE_LIMIT;
  const showFilters = !limit;

  useEffect(() => {
    if (!isAuthenticated) {
      setAppliedJobIds(new Set());
      return;
    }
    checkExistingApplications()
      .then((apps) => {
        const ids = new Set(apps.filter((a) => a.jobPostingId).map((a) => a.jobPostingId!));
        setAppliedJobIds(ids);
      })
      .catch(() => {
        /* ignore */
      });
  }, [isAuthenticated, checkExistingApplications]);

  const visiblePostings = useMemo(
    () => postings.filter((p) => !appliedJobIds.has(p.jobPostingId)),
    [postings, appliedJobIds],
  );

  const fetchPostings = useCallback(async (query: ActiveJobPostingsQuery) => {
    setIsLoading(true);
    setHasError(false);

    try {
      const response = await jobPostingApi.listActive(query);
      setPostings(response.data.items);
      setMeta(response.data.meta);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const [sortBy, sortOrder] = sortValue.split(':') as [
      ActiveJobPostingsQuery['sortBy'],
      ActiveJobPostingsQuery['sortOrder'],
    ];

    const query: ActiveJobPostingsQuery = {
      page,
      limit: pageLimit,
      sortBy,
      sortOrder,
    };

    if (search) query.q = search;
    if (selectedRole) query.role = selectedRole;
    if (selectedState) query.siteState = selectedState;
    if (selectedCity) query.siteCity = selectedCity;
    if (dateFrom) query.from = dateFrom;
    if (dateTo) query.to = dateTo;

    void fetchPostings(query);
  }, [dateFrom, dateTo, fetchPostings, page, pageLimit, search, selectedCity, selectedRole, selectedState, sortValue]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 300);
  }, []);

  const handleCityChange = useCallback((value: string) => {
    setCityInput(value);
    clearTimeout(cityDebounceRef.current);
    cityDebounceRef.current = setTimeout(() => {
      setSelectedCity(value);
      setPage(1);
    }, 300);
  }, []);

  const handleFilterChange = useCallback(
    (setter: (value: string) => void) => (value: string) => {
      setter(value);
      setPage(1);
    },
    [],
  );

  const hasFilters = search || selectedRole || selectedState || selectedCity || dateFrom || dateTo;

  return (
    <div className='min-h-[400px] space-y-6'>
      {showFilters && (
        <div className='flex flex-col gap-3'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
            <div className='relative flex-1'>
              <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' aria-hidden='true' />
              <input
                type='text'
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t('jobPostings.searchPlaceholder')}
                aria-label={t('jobPostings.filters.searchAriaLabel')}
                className='w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
              />
            </div>

            <select
              value={sortValue}
              onChange={(e) => handleFilterChange(setSortValue)(e.target.value)}
              className='min-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </select>
          </div>

          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap'>
            <select
              value={selectedRole}
              onChange={(e) => handleFilterChange(setSelectedRole)(e.target.value)}
              className='min-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'>
              <option value=''>{t('jobPostings.filters.allRoles')}</option>
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>
                  {t(`jobPostings.roles.${role}`)}
                </option>
              ))}
            </select>

            <select
              value={selectedState}
              onChange={(e) => handleFilterChange(setSelectedState)(e.target.value)}
              className='min-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'>
              <option value=''>{t('jobPostings.filters.allStates')}</option>
              {STATE_OPTIONS.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>

            <input
              type='text'
              value={cityInput}
              onChange={(e) => handleCityChange(e.target.value)}
              placeholder={t('jobPostings.filters.cityPlaceholder')}
              className='rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:w-40'
            />

            <div className='flex items-center gap-2'>
              <input
                type='date'
                value={dateFrom}
                onChange={(e) => handleFilterChange(setDateFrom)(e.target.value)}
                className='min-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                placeholder={t('jobPostings.filters.from')}
              />
              <span className='text-sm text-gray-400'>-</span>
              <input
                type='date'
                value={dateTo}
                onChange={(e) => handleFilterChange(setDateTo)(e.target.value)}
                className='min-w-[140px] rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                placeholder={t('jobPostings.filters.to')}
              />
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div
          role='status'
          aria-label={t('common.loading')}
          className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
          {Array.from({ length: limit ?? 3 }, (_, i) => (
            <div key={i} className='h-48 animate-pulse rounded-lg border border-gray-200 bg-gray-100' />
          ))}
        </div>
      ) : hasError ? (
        <div className='flex flex-col items-center gap-4 py-12 text-center'>
          <p className='text-gray-600'>{t('jobPostings.errorLoading')}</p>
          <button
            type='button'
            onClick={() => void fetchPostings({ page, limit: pageLimit })}
            className='rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-dark'>
            {t('jobPostings.retry')}
          </button>
        </div>
      ) : visiblePostings.length === 0 ? (
        <div className='py-12 text-center'>
          <p className='text-gray-600'>{hasFilters ? t('jobPostings.noResults') : t('jobPostings.empty')}</p>
        </div>
      ) : (
        <>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
            {visiblePostings.map((posting) => (
              <JobPostingCard key={posting.jobPostingId} posting={posting} />
            ))}
          </div>

          {showFilters && meta && meta.totalPages > 1 && (
            <div className='flex items-center justify-between pt-4'>
              <p className='text-sm text-gray-500'>
                {t('jobPostings.pagination.showing', {
                  from: (meta.page - 1) * meta.limit + 1,
                  to: Math.min(meta.page * meta.limit, meta.total),
                  total: meta.total,
                })}
              </p>
              <div className='flex items-center gap-2'>
                <button
                  type='button'
                  aria-label={t('common.pagination.previous')}
                  className={cn(
                    'rounded-md border border-gray-300 p-1.5',
                    meta.page <= 1 ? 'cursor-not-allowed text-gray-300' : 'text-gray-600 hover:bg-gray-50',
                  )}
                  disabled={meta.page <= 1}
                  onClick={() => setPage(meta.page - 1)}>
                  <ChevronLeft aria-hidden='true' className='h-4 w-4' />
                </button>
                <span className='text-sm text-gray-700'>
                  {t('jobPostings.pagination.page', { current: meta.page, total: meta.totalPages })}
                </span>
                <button
                  type='button'
                  aria-label={t('common.pagination.next')}
                  className={cn(
                    'rounded-md border border-gray-300 p-1.5',
                    meta.page >= meta.totalPages
                      ? 'cursor-not-allowed text-gray-300'
                      : 'text-gray-600 hover:bg-gray-50',
                  )}
                  disabled={meta.page >= meta.totalPages}
                  onClick={() => setPage(meta.page + 1)}>
                  <ChevronRight aria-hidden='true' className='h-4 w-4' />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
